import { IDictionaryData } from '@models/dictionary/IDictionaryData';

export type DictionarySelectableLevels =
    | { mode: 'all' | 'first' | 'last' | 'invalid' }
    | { mode: 'range'; min: number; max: number };

/** first/last сохраняют прежнюю семантику; в диапазоне это границы 1/∞. */
export const parseDictionarySelectableLevels = (value?: string | null): DictionarySelectableLevels => {
    const text = (value ?? '').trim();
    if (!text) return { mode: 'all' };
    if (text === 'first' || text === 'last') return { mode: text };
    const parts = text.split('-').map((part) => part.trim());
    if (parts.length > 2 || (parts[0] !== 'first' && !/^[1-9]\d*$/.test(parts[0])) ||
        (parts.length === 2 && parts[1] !== 'last' && !/^[1-9]\d*$/.test(parts[1]))) {
        return { mode: 'invalid' };
    }
    const min = parts[0] === 'first' ? 1 : Number(parts[0]);
    const max = parts[1] === 'last' ? Infinity : parts.length === 2 ? Number(parts[1]) : min;
    if (!Number.isSafeInteger(min) || (max !== Infinity && !Number.isSafeInteger(max)) || min > max) {
        return { mode: 'invalid' };
    }
    return { mode: 'range', min, max };
};

export const isDictionaryLevelSelectable = (
    rule: DictionarySelectableLevels,
    level: number | undefined,
    first: boolean,
    leaf: boolean,
): boolean => {
    switch (rule.mode) {
        case 'all': return true;
        case 'first': return first;
        case 'last': return leaf;
        case 'invalid': return false;
        case 'range': return level !== undefined && level >= rule.min && level <= rule.max;
    }
};

export type DictionarySelectionRow = IDictionaryData & { selectionLevel?: number };

/** Полный путь запрашивается пакетом: parent в tree/items может быть обнулён отбором. */
export const withDictionarySelectionLevels = async (
    rows: IDictionaryData[],
    rule: DictionarySelectableLevels,
    isFormData: boolean,
    loadPaths: (codes: string[]) => Promise<{ data: IDictionaryData[] }>,
): Promise<DictionarySelectionRow[]> => {
    if (rule.mode !== 'range' || rows.length === 0) return rows;
    if (isFormData) return rows.map((row) => ({ ...row, selectionLevel: 1 }));
    const response = await loadPaths([...new Set(rows.map((row) => row.code))]);
    const levels = new Map(response.data.map((row) => [
        row.id,
        row.fullId ? row.fullId.split('/').filter(Boolean).length : undefined,
    ]));
    // Неизвестный путь не даёт разрешения выбрать узел по предположительному уровню.
    return rows.map((row) => ({ ...row, selectionLevel: levels.get(row.id) }));
};
